/**
 * Pure helpers for the Agenda page: checking `/api/activity`'s answer,
 * telling its failures apart, and deriving what the page prints — times,
 * schedule descriptions, and the chat commands an owner copies.
 *
 * No React / DOM imports, and no runtime imports at all (the one import is
 * type-only), so the module can be loaded and checked on its own.
 *
 * **The commands are machinai's own syntax, copied from its source**
 * (`machinai/core/runtime/_commands.py`, core 0.61.0):
 *
 * - `/approvals approve <id> [session|hour] [reason]`, `deny <id>` and
 *   `revoke <grant>` take any unambiguous prefix of the id. The page
 *   prints the shortest prefix of at least eight characters that no other
 *   id on the page shares, so a printed command never answers "ambiguous".
 * - `/goals pause|resume|clear <id>` matches the id **exactly**
 *   (`GoalStore.get`), so goal commands carry the whole twelve characters.
 * - Each is answered by the agent that owns the record; another agent's
 *   approval is refused there. The page groups commands under their agent.
 */

import type {
  ActivityResponse,
  DecidedApproval,
  Goal,
  PendingApproval,
  Schedule,
} from '../../api/activity';

/* -- the answer, checked at the edge -------------------------------- */

type Obj = Record<string, unknown>;

function isObj(value: unknown): value is Obj {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** What one field must be. `?` admits `null` as well. */
type FieldKind =
  | 'string'
  | 'string?'
  | 'number'
  | 'number?'
  | 'boolean'
  | 'object'
  | 'strings';

/*
 * Every field the contract names, for each kind of row. The unions —
 * goal status, decision, grant scope, schedule kind — are checked as
 * strings and not against their members: a value this page has not met
 * renders as itself, rather than refusing an otherwise good answer.
 */
const GOAL_FIELDS: Record<string, FieldKind> = {
  id: 'string',
  objective: 'string',
  status: 'string',
  may_write: 'boolean',
  max_turns: 'number',
  turns_used: 'number',
  created_ms: 'number',
  deadline_ms: 'number',
  next_run_ms: 'number',
  last_report: 'string',
  last_summary: 'string',
  updated_ms: 'number',
};

const PENDING_FIELDS: Record<string, FieldKind> = {
  id: 'string',
  tool_name: 'string',
  tool_args: 'object',
  prompt: 'string',
  session_id: 'string',
  channel_id: 'string',
  message_source: 'string',
  created_ms: 'number',
  expires_ms: 'number',
  gates: 'strings',
  grantable: 'boolean',
};

const DECIDED_FIELDS: Record<string, FieldKind> = {
  id: 'string',
  tool_name: 'string',
  state: 'string',
  resolved_by: 'string',
  resolution_reason: 'string',
  created_ms: 'number',
  resolved_ms: 'number?',
};

const GRANT_FIELDS: Record<string, FieldKind> = {
  id: 'string',
  scope: 'string',
  tool_name: 'string',
  gates: 'strings',
  session_id: 'string?',
  approval_id: 'string',
  created_by: 'string',
  created_ms: 'number',
  expires_ms: 'number',
  uses: 'number',
};

const SCHEDULE_FIELDS: Record<string, FieldKind> = {
  id: 'string',
  name: 'string',
  kind: 'string',
  time: 'string',
  days: 'strings',
  once_date: 'string?',
  enabled: 'boolean',
  last_run: 'string?',
};

function shapeError(path: string, problem: string): Error {
  return new Error(
    `/api/activity answered in an unexpected shape: ${path} ${problem}. ` +
      'The panel and the core disagree about the route.',
  );
}

function fits(value: unknown, kind: FieldKind): boolean {
  switch (kind) {
    case 'string':
      return typeof value === 'string';
    case 'string?':
      return value === null || typeof value === 'string';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'number?':
      return (
        value === null || (typeof value === 'number' && Number.isFinite(value))
      );
    case 'boolean':
      return typeof value === 'boolean';
    case 'object':
      return isObj(value);
    case 'strings':
      return Array.isArray(value) && value.every((v) => typeof v === 'string');
  }
}

function checkRows(
  value: unknown,
  fields: Record<string, FieldKind>,
  path: string,
): void {
  if (!Array.isArray(value)) throw shapeError(path, 'is not a list');
  value.forEach((row: unknown, i) => {
    if (!isObj(row)) throw shapeError(`${path}[${i}]`, 'is not an object');
    for (const [name, kind] of Object.entries(fields)) {
      if (!fits(row[name], kind)) {
        throw shapeError(`${path}[${i}].${name}`, `is not ${kind}`);
      }
    }
  });
}

/**
 * Check one section's `available` / `reason`, and its lists when it is
 * available. An unavailable section's lists are not read — the contract
 * sends them empty, and the page renders the reason instead — so they
 * are replaced with `[]` whatever arrived.
 */
function checkSection(
  agent: Obj,
  key: string,
  lists: Record<string, Record<string, FieldKind>>,
  path: string,
): void {
  const section = agent[key];
  const here = `${path}.${key}`;
  if (!isObj(section)) throw shapeError(here, 'is not an object');
  if (typeof section.available !== 'boolean') {
    throw shapeError(`${here}.available`, 'is not true or false');
  }
  if (section.reason !== null && typeof section.reason !== 'string') {
    throw shapeError(`${here}.reason`, 'is neither text nor null');
  }
  for (const [list, fields] of Object.entries(lists)) {
    if (section.available) {
      checkRows(section[list], fields, `${here}.${list}`);
    } else {
      section[list] = [];
    }
  }
}

/**
 * The answer, or an error naming the first field that does not fit.
 *
 * A route built in parallel with this page is the likeliest place for the
 * two to disagree, and a disagreement found at render time is a
 * `TypeError` that, with no error boundary, blanks the whole panel. Found
 * here, it is a sentence on this page.
 */
export function parseActivity(body: unknown): ActivityResponse {
  if (!isObj(body)) throw shapeError('the body', 'is not an object');
  if (!fits(body.now_ms, 'number')) throw shapeError('now_ms', 'is not number');
  if (!Array.isArray(body.agents)) throw shapeError('agents', 'is not a list');
  body.agents.forEach((agent: unknown, i) => {
    const path = `agents[${i}]`;
    if (!isObj(agent)) throw shapeError(path, 'is not an object');
    if (typeof agent.agent_id !== 'string') {
      throw shapeError(`${path}.agent_id`, 'is not string');
    }
    checkSection(agent, 'goals', { open: GOAL_FIELDS, ended: GOAL_FIELDS }, path);
    checkSection(
      agent,
      'approvals',
      { pending: PENDING_FIELDS, decided: DECIDED_FIELDS },
      path,
    );
    checkSection(agent, 'grants', { live: GRANT_FIELDS }, path);
    checkSection(agent, 'schedules', { entries: SCHEDULE_FIELDS }, path);
  });
  return body as unknown as ActivityResponse;
}

/* -- failures --------------------------------------------------------- */

/**
 * Why there is no answer, in the three ways the page words differently.
 *
 * `missing` is 404: a core older than 0.62.0, which has no such route.
 * `forbidden` is 403: the signed-in role is below `editor`. Anything else
 * is shown as it came.
 */
export type ActivityFailure =
  | { kind: 'missing' }
  | { kind: 'forbidden' }
  | { kind: 'failed'; message: string };

/**
 * Classify a rejected fetch by its `status`.
 *
 * Read from a `status` property rather than with `instanceof ApiError`,
 * which would make this module import the client at runtime. Anything
 * without a numeric status — a network failure, a shape error from
 * `parseActivity` — is `failed`, with its own message.
 */
export function classifyFailure(err: unknown): ActivityFailure {
  const status = isObj(err) ? err.status : undefined;
  if (status === 404) return { kind: 'missing' };
  if (status === 403) return { kind: 'forbidden' };
  return {
    kind: 'failed',
    message: err instanceof Error ? err.message : String(err),
  };
}

/* -- time -------------------------------------------------------------- */

/** A span as one unit, floored: `45s`, `12m`, `3h`, `2d`. */
function span(ms: number): string {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

/**
 * `target` against `now`: `3h ago`, `in 2d`, or `now` within a second.
 *
 * Floored, so a deadline 1 h 59 m away reads `in 1h` — the reading errs
 * toward less time left, never more. The exact time belongs in the
 * element's `title` (`absoluteTime`). A non-finite input reads `unknown`
 * rather than `NaNd ago`.
 */
export function relativeTime(targetMs: number, nowMs: number): string {
  if (!Number.isFinite(targetMs) || !Number.isFinite(nowMs)) return 'unknown';
  const diff = targetMs - nowMs;
  if (Math.abs(diff) < 1000) return 'now';
  return diff > 0 ? `in ${span(diff)}` : `${span(-diff)} ago`;
}

/** The exact time, in the viewer's locale and zone, for a `title`. */
export function absoluteTime(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return 'unknown time';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/* -- ids and commands ----------------------------------------------- */

/**
 * The shortest prefix of `id`, at least `min` long, that no other id in
 * `all` starts with — or the whole id when no shorter one is unique.
 *
 * `all` should be every id the command could be confused with. The page
 * passes the ids of every agent's rows, a superset of what the core
 * compares against, so the prefix it prints is unique where it counts.
 */
export function uniquePrefix(
  id: string,
  all: readonly string[],
  min = 8,
): string {
  const others = all.filter((other) => other !== id);
  for (let n = Math.max(1, min); n < id.length; n += 1) {
    const prefix = id.slice(0, n);
    if (!others.some((other) => other.startsWith(prefix))) return prefix;
  }
  return id;
}

/** Every pending approval's id, across agents, for `uniquePrefix`. */
export function pendingIds(resp: ActivityResponse): string[] {
  return resp.agents.flatMap((a) => a.approvals.pending.map((p) => p.id));
}

/** Every live grant's id, across agents, for `uniquePrefix`. */
export function grantIds(resp: ActivityResponse): string[] {
  return resp.agents.flatMap((a) => a.grants.live.map((g) => g.id));
}

/** How many approvals wait on the owner, across every agent. */
export function waitingCount(resp: ActivityResponse): number {
  return resp.agents.reduce(
    (n, a) => n + (a.approvals.available ? a.approvals.pending.length : 0),
    0,
  );
}

/**
 * Whether a pending approval has passed its expiry. The core refuses to
 * approve one at or past `expires_ms` and resolves it to deny.
 */
export function isLapsed(approval: PendingApproval, nowMs: number): boolean {
  return approval.expires_ms <= nowMs;
}

/**
 * The commands that answer one pending approval, given its printed prefix.
 *
 * A lapsed approval gets only `deny`: approving it is refused, and deny
 * records the outcome and unblocks the session. A grantable one adds the
 * two standing-grant forms; one that is not is answerable once only.
 */
export function approvalCommands(
  approval: PendingApproval,
  prefix: string,
  nowMs: number,
): string[] {
  const deny = `/approvals deny ${prefix}`;
  if (isLapsed(approval, nowMs)) return [deny];
  const approve = `/approvals approve ${prefix}`;
  return approval.grantable
    ? [approve, `${approve} session`, `${approve} hour`, deny]
    : [approve, deny];
}

/** The command that ends a standing grant early. */
export function revokeCommand(prefix: string): string {
  return `/approvals revoke ${prefix}`;
}

/**
 * The owner's controls for one goal, with its whole id. An ended goal has
 * none: the store refuses to change it.
 */
export function goalCommands(goal: Goal): string[] {
  if (goal.status === 'active') {
    return [`/goals pause ${goal.id}`, `/goals clear ${goal.id}`];
  }
  if (goal.status === 'paused') {
    return [`/goals resume ${goal.id}`, `/goals clear ${goal.id}`];
  }
  return [];
}

/* -- wording ---------------------------------------------------------- */

/** When a schedule fires, in the words `schedule_list` uses. */
export function scheduleWhen(s: Schedule): string {
  if (s.once_date) return `once on ${s.once_date} at ${s.time}`;
  if (s.days.length > 0) return `every ${s.days.join(', ')} at ${s.time}`;
  return `daily at ${s.time}`;
}

/** When a schedule last ran. `null` and `""` both mean it has not. */
export function scheduleLastRun(s: Schedule): string {
  return s.last_run ? `last ran ${s.last_run}` : 'has not run';
}

/**
 * Who decided an approval. `grant:<id>` is a standing grant answering
 * without asking, and `""` is a decision nothing attributed.
 */
export function decidedBy(d: DecidedApproval): {
  label: string;
  grantId: string | null;
} {
  if (d.resolved_by.startsWith('grant:')) {
    return { label: 'standing grant', grantId: d.resolved_by.slice(6) };
  }
  if (d.resolved_by === '') return { label: 'unattributed', grantId: null };
  return { label: d.resolved_by, grantId: null };
}

/** Per-value and whole-line caps, the core's own (`_commands.py`). */
const VALUE_CHARS = 80;
const PREVIEW_CHARS = 240;

/**
 * Bound `text`, saying how much was left out. Silent truncation would let
 * an owner answer a call whose arguments they believe they read in full.
 */
export function elide(text: string, limit: number): string {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}… (+${text.length - limit} chars elided)`;
}

/**
 * A held call's arguments on one bounded line: `key=<json>, …`, each
 * value and the whole line capped, whitespace collapsed — as `/approvals
 * list` renders them. The page shows the full arguments beside it.
 */
export function argPreview(args: Record<string, unknown>): string {
  const entries = Object.entries(args);
  if (entries.length === 0) return '(none)';
  const parts = entries.map(([key, value]) => {
    const rendered = (JSON.stringify(value) ?? String(value))
      .split(/\s+/)
      .filter(Boolean)
      .join(' ');
    return `${key}=${elide(rendered, VALUE_CHARS)}`;
  });
  return elide(parts.join(', '), PREVIEW_CHARS);
}

/** `1 turn`, `3 turns`. */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}
